
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import AuthContext from './AuthContext';
import {
    getAssignment,
    getClassroomAssignments as fetchClassroomAssignmentsAPI,
    createAssignment as createAssignmentAPI,
    updateAssignment as updateAssignmentAPI,
    deleteAssignment as deleteAssignmentAPI,
} from '../services/AssignmentServices';
import { useNavigate } from 'react-router-dom';
import * as ClassroomAPI from '../services/ClassroomServices';
import { getJoinedClassrooms } from '../services/UserServices';

export const ClassroomContext = createContext();

export const ClassroomContextProvider = ({ children }) => {
    const { userId } = useContext(AuthContext);
    const [classrooms, setClassrooms] = useState([]);
    const [joinedClassrooms, setJoinedClassrooms] = useState([]);
    const [classroom, setClassroom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMember, setIsMember] = useState(null);
    const [isTeacher, setIsTeacher] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [isFetchingClassroomDetails, setIsFetchingClassroomDetails] = useState(false);
    const [isFetchingAssignments, setIsFetchingAssignments] = useState(false);

    
    const [classroomAssignments, setClassroomAssignments] = useState([]);
    const [classroomAssignment, setClassroomAssignment] = useState(null);
    const [allMyAssignments, setAllMyAssignments] = useState([]);
    const [classroomAssignmentSubmissions, setClassroomAssignmentSubmissions] = useState([]);
    const [classroomAssignmentSubmission, setClassroomAssignmentSubmission] = useState([]);
    


    const navigate = useNavigate();

    
    const clearCurrentClassroom = useCallback(() => {
        setClassroom(null);
        setClassroomAssignments([]);
        setIsTeacher(false);
        setIsOwner(false);
    }, []);

    const fetchClassrooms = useCallback(async (currentUserId) => {
        if (!currentUserId) return;
        setLoading(true);
        try {
            const [classroomsData, joinedData] = await Promise.all([
                ClassroomAPI.getClassroomsByUser(currentUserId),
                getJoinedClassrooms(currentUserId),
            ]);
            setClassrooms(classroomsData);
            setJoinedClassrooms(joinedData.joinedClassrooms);
        } catch (error) {
            console.error('Error fetching classrooms:', error);
            setClassrooms([]);
            setJoinedClassrooms([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            fetchClassrooms(userId);
        } else {
            setClassrooms([]);
            setJoinedClassrooms([]);
            setClassroom(null);
            setClassroomAssignments([]);
            setLoading(false);
        }
    }, [userId, fetchClassrooms]);

    const createClassroom = useCallback(async (classroomData) => {
        const formData = new FormData();
        formData.append('name', classroomData.name);
        if (classroomData.image) {
            formData.append('image', classroomData.image);
        }
        const newClassroom = await ClassroomAPI.createClassroom(formData);
        setClassrooms((prevClassrooms) => [...prevClassrooms, newClassroom]);
        return newClassroom;
    }, []);

     const fetchClassroomAssignmentsInternal = useCallback(async (classroomIdToFetch) => {
        if (!classroomIdToFetch) {
            setClassroomAssignments([]);
            return { success: true, data: [] };
        }
        setIsFetchingAssignments(true);
        try {
            const data = await fetchClassroomAssignmentsAPI(classroomIdToFetch);
            setClassroomAssignments(data);
            return { success: true, data };
        } catch (error) {
            console.error(`Error fetching assignments for classroom ${classroomIdToFetch}:`, error);
            setClassroomAssignments([]);
            return { success: false, error };
        } finally {
            setIsFetchingAssignments(false);
        }
    }, []);

    const fetchClassroom = useCallback(async (classroomIdToFetch, currentUserId) => {
        if (!classroomIdToFetch || classroomIdToFetch === 'undefined') { 
            clearCurrentClassroom();
            setIsFetchingClassroomDetails(false);
            return;
        }

        if (classroom?._id !== classroomIdToFetch) {
            clearCurrentClassroom();
            setIsMember(null); 
        }
        setIsFetchingClassroomDetails(true);

        try {
            const classroomData = await ClassroomAPI.getClassroom(classroomIdToFetch);
            const owner = classroomData.owner._id === currentUserId;
            const teacher = classroomData.teachers.some(t => t._id === currentUserId);
            const student = classroomData.students.some(s => s._id === currentUserId); 

            if (owner || teacher || student) {
                setClassroom(classroomData);
                setIsOwner(owner);
                setIsTeacher(teacher);
                setIsMember(true); 
                await fetchClassroomAssignmentsInternal(classroomIdToFetch);
            } else {
                clearCurrentClassroom();
                setIsMember(false); 
            }
        } catch (error) {
            console.error(`Error fetching classroom ${classroomIdToFetch}:`, error);
            clearCurrentClassroom();
            setIsMember(false);
        } finally {
            setIsFetchingClassroomDetails(false);
        }
    }, [classroom?._id, fetchClassroomAssignmentsInternal, clearCurrentClassroom]);

    const fetchClassroomAssignments = fetchClassroomAssignmentsInternal;

    const createAssignment = useCallback(async (classroomId, assignmentData, currentUserId) => {
        const newAssignment = await createAssignmentAPI(classroomId, assignmentData);
        setClassroomAssignments(prev => [...prev, newAssignment]);
        return newAssignment;
    }, []);

    const updateAssignment = useCallback(async (assignmentId, updateData) => {
        const updatedAssignment = await updateAssignmentAPI(assignmentId, updateData);
        setClassroomAssignments(prev => prev.map(a => a._id === assignmentId ? updatedAssignment : a));
        if (classroomAssignment?._id === assignmentId) {
            setClassroomAssignment(updatedAssignment);
        }
        return updatedAssignment;
    }, [classroomAssignment]);

    const deleteAssignment = useCallback(async (assignmentId) => {
        try {
            await deleteAssignmentAPI(assignmentId);
            setClassroomAssignments(prev =>
                prev.filter(assignment => assignment._id !== assignmentId)
            );
            if (classroomAssignment && classroomAssignment._id === assignmentId) {
                setClassroomAssignment(null);
            }
        } catch (error) {
            console.error('Error deleting assignment:', error);
            throw error;
        }
    }, [classroomAssignment]);

    const fetchAssignment = useCallback(async (assignmentId) => {
        try {
            const assignmentData = await getAssignment(assignmentId);
            setClassroomAssignment(assignmentData);
        } catch (error) {
            console.error('Error fetching assignment:', error);
        }
    }, []);

    const updateClassroom = useCallback(async (id, updates) => {
        try {
            const updatedClassroomFromServer = await ClassroomAPI.updateClassroom(id, updates);

            setClassrooms(prevClassrooms =>
                prevClassrooms.map(c =>
                    c._id === id ? updatedClassroomFromServer : c
                )
            );
            setJoinedClassrooms(prevJoinedClassrooms =>
                prevJoinedClassrooms.map(jc =>
                    jc._id === id ? updatedClassroomFromServer : jc
                )
            );
            if (classroom && classroom._id === id) {
                setClassroom(updatedClassroomFromServer);
            }
        } catch (error) {
            console.error('Error updating classroom:', error);
            throw error;
        }
    }, [classrooms, joinedClassrooms, classroom]);

    const deleteClassroom = useCallback(async (classroomId) => {
        try {
            await ClassroomAPI.deleteClassroom(classroomId);
            if (classroom && classroom._id === classroomId) {
                setClassroom(null);
            }
            setClassrooms(prevClassrooms => prevClassrooms.filter(c => c._id !== classroomId));
            setJoinedClassrooms(prevJoinedClassrooms => prevJoinedClassrooms.filter(jc => jc._id !== classroomId));
            navigate("/my-classrooms");
        } catch (error) {
            console.error("Failed to delete classroom:", error);
            throw error;
        }
    }, [navigate, classroom]);


    
    const contextValue = React.useMemo(() => ({
        classrooms,
        joinedClassrooms,
        classroom,
        setJoinedClassrooms,
        setClassroom, 
        fetchClassrooms,
        createClassroom,
        fetchClassroom,
        updateClassroom,
        deleteClassroom,
        isOwner,
        setClassrooms,
        isTeacher,
        loading,
        isFetchingClassroomDetails,
        isFetchingAssignments,
        fetchClassroomAssignments,
        createAssignment,
        updateAssignment,
        deleteAssignment,
        classroomAssignments,
        classroomAssignment,
        setClassroomAssignment,
        allMyAssignments,
        classroomAssignmentSubmissions,
        setClassroomAssignmentSubmissions,
        classroomAssignmentSubmission,
        setClassroomAssignmentSubmission,
        clearCurrentClassroom,
        isMember,
    }), [
        classrooms, joinedClassrooms, classroom, fetchClassrooms, createClassroom, fetchClassroom,
        updateClassroom, deleteClassroom, isOwner, setClassrooms, isTeacher, loading, isFetchingClassroomDetails,
        isFetchingAssignments, fetchClassroomAssignments,
        createAssignment, updateAssignment, deleteAssignment, classroomAssignments,
        classroomAssignment, setClassroomAssignment, classroomAssignmentSubmissions,
        setClassroomAssignmentSubmissions, classroomAssignmentSubmission, setClassroomAssignmentSubmission,
        clearCurrentClassroom, isMember
    ]);


    return (
        <ClassroomContext.Provider value={contextValue}>
            {children}
        </ClassroomContext.Provider>
    );
};
